/**
 * The looks a studio can give its client galleries.
 *
 * A theme is two separable things kept behind one choice: a LAYOUT (how photos
 * are arranged) and a SKIN (the palette, display face and the little mark that
 * sits above each caption). Only 'grid' changes the layout — every other theme
 * is the magazine spread wearing different clothes, because the arrangement is
 * what makes a gallery read as a story and the palette is what makes it feel
 * like this particular occasion.
 *
 * The skin itself lives in PersonGallery.css as `.pg-theme--<key>`, which sets
 * the --pg-* custom properties the whole page is drawn from. Adding a theme is
 * an entry here plus that one CSS block.
 *
 * `key` is what is stored on the event and validated by the API, so this list
 * and GALLERY_THEMES in services/api/server.js must agree — there is a test
 * pinning that.
 */

export const GALLERY_THEMES = [
  {
    key: 'magazine',
    name: 'Magazine',
    desc: 'Editorial spread with words between photos',
    layout: 'magazine',
    mark: '✦',
    swatch: ['#FAF8F5', '#a53ec2', '#1A1720'],
  },
  {
    key: 'grid',
    name: 'Grid',
    desc: 'Photos only, in neat columns',
    layout: 'grid',
    mark: '✦',
    swatch: ['#FAF8F5', '#6B6572', '#1A1720'],
  },
  {
    key: 'kids',
    name: 'Kids',
    desc: 'Bunting, a rainbow and confetti',
    layout: 'magazine',
    mark: '★',
    swatch: ['#FFF6E9', '#FF7A3D', '#2B2140'],
  },
  {
    key: 'baby-shower',
    name: 'Baby shower',
    desc: 'Clouds, a soft moon and tiny stars',
    layout: 'magazine',
    mark: '❀',
    swatch: ['#FBF7FB', '#C89BD6', '#3B3450'],
  },
  {
    key: 'temple',
    name: 'Temple',
    desc: 'A mandala, marigold garland and lamplight',
    layout: 'magazine',
    mark: 'ॐ',
    swatch: ['#FFF8EC', '#C8842B', '#4A1D1D'],
  },
  {
    key: 'church',
    name: 'Church',
    desc: 'A rose window, gothic arches, falling light',
    layout: 'magazine',
    mark: '✝',
    swatch: ['#FBFAF7', '#B08A46', '#1F2A44'],
  },
  {
    key: 'birthday',
    name: 'Birthday',
    desc: 'Streamers, balloons and confetti',
    layout: 'magazine',
    mark: '✧',
    swatch: ['#FFF8FA', '#E0417C', '#33203A'],
  },
  {
    key: 'beach',
    name: 'Beach',
    desc: 'Sun, surf and a strip of sand',
    layout: 'magazine',
    mark: '≈',
    swatch: ['#F7FBFB', '#2E9CAE', '#123A44'],
  },
  {
    key: 'hills',
    name: 'Hills',
    desc: 'Ridge behind ridge, mist and pines',
    layout: 'magazine',
    mark: '▲',
    swatch: ['#F6F8F5', '#5C7F62', '#22302A'],
  },
];

export const THEME_KEYS = GALLERY_THEMES.map((t) => t.key);

const BY_KEY = new Map(GALLERY_THEMES.map((t) => [t.key, t]));

// An unknown or missing theme is the magazine — that is what every event
// showed before themes existed, and what a stored value we no longer render
// should fall back to rather than a broken page.
export const themeOf = (key) => BY_KEY.get(key) || BY_KEY.get('magazine');

export const isGridLayout = (key) => themeOf(key).layout === 'grid';

/** Class applied to the gallery root; drives the whole palette. */
export const themeClass = (key) => `pg-theme--${themeOf(key).key}`;
